/**
 * Generates a UUID v4 string without requiring a secure context.
 *
 * `crypto.randomUUID()` is only available in secure contexts (HTTPS / localhost).
 * This helper tries that first, then falls back to `crypto.getRandomValues()`
 * (available in non-secure HTTP contexts), and finally to a `Math.random()`-based
 * implementation as a last resort.
 */
export function generateUUID(): string {
  // Preferred: native randomUUID (secure context only)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback: crypto.getRandomValues – available in HTTP contexts
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c => {
      const n = Number(c);
      return (n ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (n / 4)))).toString(16);
    });
  }

  // Last resort: Math.random-based UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
